import { AnyAction, Reducer } from 'redux';
import { action } from 'typesafe-actions';
import { AuthStatus } from '../constants/AuthStatus';
import { CurrentUserState } from '../types/CurrentUserState';
import { Dealer } from '../types/Dealer';
import { Group } from '../types/Group';
import { User } from '../types/User';
import { Configurator } from '../types/Configurator';
import { defaultGroupSettings } from '../constants/Group';
import { GroupMember } from '../types/GroupMember';
import { UserPreference } from '../constants/User';
import { userEventAction } from '../analytics/AnalyticsTypes';

// Actions types

export enum CurrentUserActionTypes {
  SEND_FORGOT_PASSWORD_CODE = 'SEND_FORGOT_PASSWORD_CODE',
  SET_CURRENT_USER_DATA = 'SET_CURRENT_USER_DATA',
  SET_CURRENT_USER_AUTH_STATUS = 'SET_CURRENT_USER_AUTH_STATUS',
  SET_CURRENT_USER_AUTH_PROVIDER = 'SET_CURRENT_USER_AUTH_PROVIDER',
  SIGN_OUT = 'SIGN_OUT',
  SIGNED_IN = 'SIGNED_IN',
  SIGNED_IN_SUCCESS = 'SIGNED_IN_SUCCESS',
  SIGNED_IN_FAILED = 'SIGNED_IN_FAILED',
  SET_SIGNING_IN = 'SET_SIGNING_IN',
  UPDATE_USER = 'UPDATE_USER',
  CHANGE_GROUP = 'CHANGE_GROUP',
  SET_AVAILABLE_DEALER = 'SET_AVAILABLE_DEALER',
  OAUTH_SIGN_IN = 'OAUTH_SIGN_IN',
  SET_ERRORS = 'SET_ERRORS',
  CLEAR_ERRORS = 'CLEAR_ERRORS',
  SET_GROUP_CONFIG = 'SET_GROUP_CONFIG',
  UPDATE_GROUP_CONFIG = 'UPDATE_GROUP_CONFIG',
  FETCH_CURRENT_USER_DATA = 'FETCH_CURRENT_USER_DATA',
  INITIATE_IMPERSONATION = 'INITIATE_IMPERSONATION',
  VALIDATE_IMPERSONATION = 'VALIDATE_IMPERSONATION',
  IMPERSONATION_FAILED = 'IMPERSONATION_FAILED',
  IMPERSONATION_SUCCESS = 'IMPERSONATION_SUCCESS',
  SET_USER_PREFERENCES = 'SET_USER_PREFERENCES',
  SAVE_USER_PREFERENCES = 'SAVE_USER_PREFERENCES',
  SAVE_USER_PREFERENCES_COMPLETE = 'SAVE_USER_PREFERENCES_COMPLETE',
  FETCH_USER_PREFERENCES = 'FETCH_USER_PREFERENCES',
  FETCH_USER_PREFERENCES_COMPLETE = 'FETCH_USER_PREFERENCES_COMPLETE',
}

// Reducer

export const INITIAL_CURRENT_USER_STATE: CurrentUserState = {
  authStatus: AuthStatus.SignedOut,
  authProvider: undefined,
  group: undefined,
  loading: true,
  user: undefined,
  impersonation: {},
  errors: {},
  signingIn: false,
};

export const currentUserReducer: Reducer<CurrentUserState> = (
  state = INITIAL_CURRENT_USER_STATE,
  currentUserAction,
) => {
  switch (currentUserAction.type) {
    case CurrentUserActionTypes.SET_CURRENT_USER_DATA: {
      const {
        payload: { user, groups, group, availableDealers },
      } = currentUserAction;

      return {
        ...state,
        authStatus: AuthStatus.SignedIn,
        loading: false,
        user,
        groups,
        group: {
          ...group,
          settings: {
            ...defaultGroupSettings,
            ...group.settings,
          },
        },
        availableDealers,
      };
    }

    case CurrentUserActionTypes.SET_SIGNING_IN: {
      const {
        payload: { signingIn },
      } = currentUserAction;

      return {
        ...state,
        signingIn,
      };
    }

    case CurrentUserActionTypes.SET_CURRENT_USER_AUTH_STATUS: {
      const {
        payload: { authStatus },
      } = currentUserAction;

      return { ...state, authStatus };
    }

    case CurrentUserActionTypes.SET_CURRENT_USER_AUTH_PROVIDER: {
      const {
        payload: { authProvider },
      } = currentUserAction;

      return { ...state, authProvider };
    }

    case CurrentUserActionTypes.SIGN_OUT: {
      return {
        ...INITIAL_CURRENT_USER_STATE,
      };
    }

    case CurrentUserActionTypes.UPDATE_USER: {
      const {
        payload: { user },
      } = currentUserAction;

      return { ...state, user };
    }

    case CurrentUserActionTypes.CHANGE_GROUP: {
      return {
        ...state,
        loading: true,
      };
    }

    case CurrentUserActionTypes.SET_AVAILABLE_DEALER: {
      const {
        payload: { dealers: availableDealers },
      } = currentUserAction;

      return {
        ...state,
        availableDealers,
      };
    }

    case CurrentUserActionTypes.SET_ERRORS: {
      const {
        payload: { errors },
      } = currentUserAction;

      return { ...state, errors };
    }

    case CurrentUserActionTypes.CLEAR_ERRORS: {
      return { ...state, errors: {} };
    }

    case CurrentUserActionTypes.SET_GROUP_CONFIG: {
      const {
        payload: { config },
      } = currentUserAction;
      const { group = {} } = state;
      const { configurators = [] } = group as Group;
      const index = configurators.findIndex((c) => c.clientId === config.clientId);
      const updatedConfigs = [...configurators];
      if (index !== -1) {
        updatedConfigs[index] = config;
      }
      return {
        ...state,
        group: {
          ...(state.group as Group),
          updatedConfigs,
        },
      };
    }

    case CurrentUserActionTypes.INITIATE_IMPERSONATION: {
      const {
        payload: { user },
      } = currentUserAction;
      return {
        ...state,
        impersonation: {
          user,
          loading: false,
        },
      };
    }

    case CurrentUserActionTypes.VALIDATE_IMPERSONATION: {
      return {
        ...state,
        impersonation: {
          ...state.impersonation,
          loading: true,
        },
      };
    }

    case CurrentUserActionTypes.IMPERSONATION_FAILED:
    case CurrentUserActionTypes.IMPERSONATION_SUCCESS: {
      return {
        ...state,
        impersonation: INITIAL_CURRENT_USER_STATE.impersonation,
      };
    }

    case CurrentUserActionTypes.SET_USER_PREFERENCES: {
      const {
        payload: { preferences },
      } = currentUserAction;
      return {
        ...state,
        preferences,
      };
    }

    default: {
      return state;
    }
  }
};

// Action creators

export const sendForgotPasswordCode = (): AnyAction => action(CurrentUserActionTypes.SEND_FORGOT_PASSWORD_CODE);

export const setCurrentUserData = (user: User, group: Group, groups: Group[], availableDealers: Dealer[]): AnyAction =>
  action(CurrentUserActionTypes.SET_CURRENT_USER_DATA, { user, group, groups, availableDealers });

export const setCurrentUserAuthStatus = (authStatus: AuthStatus): AnyAction =>
  action(CurrentUserActionTypes.SET_CURRENT_USER_AUTH_STATUS, { authStatus });

export const setCurrentUserAuthProvider = (authProvider?: string): AnyAction =>
  action(CurrentUserActionTypes.SET_CURRENT_USER_AUTH_PROVIDER, { authProvider });

export const signOut = (): AnyAction => action(CurrentUserActionTypes.SIGN_OUT);

export const signedIn = (): AnyAction => action(CurrentUserActionTypes.SIGNED_IN);

export const signedInSuccess = (username: string, groupId: string): AnyAction =>
  action(CurrentUserActionTypes.SIGNED_IN_SUCCESS, { username, groupId });

export const signedInFailed = (): AnyAction => action(CurrentUserActionTypes.SIGNED_IN_FAILED);

export const setSigningIn = (signingIn: boolean): AnyAction =>
  action(CurrentUserActionTypes.SET_SIGNING_IN, { signingIn });

export const updateCurrentUser = (user: User): AnyAction => action(CurrentUserActionTypes.UPDATE_USER, { user });

export const changeGroup = (groupId: string): AnyAction => action(CurrentUserActionTypes.CHANGE_GROUP, { groupId });

export const setAvailableDealers = (dealers: Dealer[]): AnyAction =>
  action(CurrentUserActionTypes.SET_AVAILABLE_DEALER, { dealers });

export const oauthSignIn = (): AnyAction => action(CurrentUserActionTypes.OAUTH_SIGN_IN);

export const setErrors = (errors: any): AnyAction => action(CurrentUserActionTypes.SET_ERRORS, { errors });

export const clearErrors = (): AnyAction => action(CurrentUserActionTypes.CLEAR_ERRORS);

export const setGroupConfig = (config: Configurator): AnyAction =>
  action(CurrentUserActionTypes.SET_GROUP_CONFIG, { config });

export const updateGroupConfig = (clientId: string): AnyAction =>
  action(CurrentUserActionTypes.UPDATE_GROUP_CONFIG, { clientId });

export const fetchCurrentUserData = (): AnyAction => action(CurrentUserActionTypes.FETCH_CURRENT_USER_DATA);

export const initiateImpersonation = (user: GroupMember): AnyAction =>
  action(CurrentUserActionTypes.INITIATE_IMPERSONATION, { user });

export const validateImpersonation = (): AnyAction => action(CurrentUserActionTypes.VALIDATE_IMPERSONATION);

export const impersonationSuccess = (): AnyAction => action(CurrentUserActionTypes.IMPERSONATION_SUCCESS);

export const impersonationFailed = (): AnyAction => action(CurrentUserActionTypes.IMPERSONATION_FAILED);

export const setUserPreferences = (preferences: any): AnyAction =>
  action(CurrentUserActionTypes.SET_USER_PREFERENCES, { preferences });

export const fetchUserPreferences = (): AnyAction => action(CurrentUserActionTypes.FETCH_USER_PREFERENCES);

export const fetchUserPreferencesComplete = (): AnyAction =>
  action(CurrentUserActionTypes.FETCH_USER_PREFERENCES_COMPLETE);

export const saveUserPreferences = (userPreference: UserPreference, preferences: any): AnyAction =>
  action(CurrentUserActionTypes.SAVE_USER_PREFERENCES, { userPreference, preferences });

export const saveUserPreferencesComplete = (): AnyAction =>
  action(CurrentUserActionTypes.SAVE_USER_PREFERENCES_COMPLETE);

export const recordUserEvent = (
  groupId: string,
  clientId: string | undefined,
  eventCategory: string | undefined,
  eventType: string,
): AnyAction => action(userEventAction, { groupId, clientId, eventCategory, eventType });
